<template>
  <div class="a2p-main">
    <div class="a2p-eyebrow">{{ eyebrow }}</div>
    <h1 class="a2p-title">Business information</h1>
    <p class="a2p-sub">
      The seven fields below are what the carriers need to register your entity.
      They must match your state/provincial business filing exactly — mismatches
      cause automatic rejection.
    </p>

    <form @submit.prevent="submit" novalidate>
      <!-- 1. Legal Business Name -->
      <A2PField
        :num="1"
        label="Legal Business Name"
        help="The <strong>exact</strong> name on your state or IRS filings, including the entity suffix (LLC, PLLC, Inc, PC, etc.). Must match your Tax ID record — mismatches cause automatic rejection."
        example="e.g. Smile Dental Group LLC"
        :value="localData.businessName"
        :validation="legalNameValidation"
      >
        <input
          v-model="localData.businessName"
          class="a2p-field-input"
          placeholder="Smile Dental Group LLC"
          required
        />
      </A2PField>

      <!-- 2. Country -->
      <A2PField
        :num="2"
        label="Country"
        help="We treat US and Canadian filings differently — pick the country that matches where your business is registered."
        :value="localData.country"
      >
        <select v-model="localData.country" class="a2p-field-select" @change="onCountryChange">
          <option value="US">United States</option>
          <option value="CA">Canada</option>
        </select>
      </A2PField>

      <!-- 3. Business Address -->
      <A2PField
        :num="3"
        label="Full Business Address"
        help="Street, suite (if any), city, state/province, and ZIP/postal code."
        example="e.g. 456 Oak Street, Suite 100, Dallas, TX 75201"
        :value="localData.businessAddress"
      >
        <input
          v-model="localData.businessAddress"
          class="a2p-field-input"
          placeholder="Street, suite, city, state, ZIP"
          required
        />
      </A2PField>

      <!-- 4. Tax ID -->
      <A2PField
        :num="4"
        :label="taxIdLabel"
        :help="taxIdHelp"
        :example="taxIdExample"
        :value="localData.ein"
        :validation="taxIdValidation"
      >
        <input
          v-model="localData.ein"
          class="a2p-field-input mono"
          :placeholder="taxIdPlaceholder"
          :maxlength="localData.country === 'CA' ? 9 : 10"
          @input="formatTaxId"
          required
        />
      </A2PField>

      <!-- 5. Business Type -->
      <A2PField
        :num="5"
        label="Business Type"
        help="Pick the one that matches your filings."
        :value="localData.businessType"
      >
        <select v-model="localData.businessType" class="a2p-field-select" required>
          <option value="">— Select —</option>
          <option value="Limited Liability Corporation">Limited Liability Corporation (LLC/PLLC)</option>
          <option value="Corporation">Corporation</option>
          <option value="Sole Proprietorship">Sole Proprietorship</option>
          <option value="Partnership">Partnership</option>
          <option value="Non-profit Corporation">Non-profit Corporation</option>
          <option value="Co-operative">Co-operative</option>
        </select>
      </A2PField>

      <!-- 6. Website URL -->
      <A2PField
        :num="6"
        label="Business Website URL"
        help="<strong>Required.</strong> The wireless carriers visit your site to verify your privacy policy and terms of service."
        :value="localData.website"
        :validation="websiteValidation"
      >
        <input
          v-model="localData.website"
          class="a2p-field-input mono"
          placeholder="https://smiledental.com"
          @input="onWebsiteInput"
          @blur="runScanNow('website')"
          required
        />
        <A2PUrlScanResult
          :state="scan.website.state"
          url-type="website"
          :result="scan.website.result"
          :error-message="scan.website.error"
          @retry="runScanNow('website')"
        />
      </A2PField>

      <!-- 7. Privacy Policy URL (optional) -->
      <A2PField
        :num="7"
        label="Privacy Policy URL"
        sub="optional"
        :help="privacyUrlHelp"
        :value="localData.privacyPolicyURL"
      >
        <input
          v-model="localData.privacyPolicyURL"
          class="a2p-field-input mono"
          placeholder="https://smiledental.com/privacy (optional)"
          @input="onPrivacyInput"
          @blur="runScanNow('privacy')"
        />
        <A2PUrlScanResult
          :state="scan.privacy.state"
          url-type="privacy"
          :result="scan.privacy.result"
          :error-message="scan.privacy.error"
          @retry="runScanNow('privacy')"
        />
      </A2PField>

      <!-- Terms of Service URL (optional) -->
      <A2PField
        :num="8"
        label="Terms of Service URL"
        sub="optional"
        :help="termsUrlHelp"
        :value="localData.termsOfServiceURL"
      >
        <input
          v-model="localData.termsOfServiceURL"
          class="a2p-field-input mono"
          placeholder="https://smiledental.com/terms (optional)"
          @input="onTermsInput"
          @blur="runScanNow('terms')"
        />
        <A2PUrlScanResult
          :state="scan.terms.state"
          url-type="terms"
          :result="scan.terms.result"
          :error-message="scan.terms.error"
          @retry="runScanNow('terms')"
        />
      </A2PField>

      <div class="a2p-section" style="margin-top: 48px">
        <div class="a2p-section-head">
          <div class="a2p-section-eyebrow">Office contact</div>
          <h2>Opt-out channels patients see</h2>
          <p class="a2p-section-desc">
            The phone and email below are baked into the SMS opt-out instructions on
            your privacy policy and terms pages. Use channels the office actually monitors —
            carriers do call and email to spot-check.
          </p>
        </div>

        <A2PField
          :num="9"
          label="Office Phone"
          help="Your practice's public phone number. Gets inserted where the compliance text says <code class='inline'>[OFFICE PHONE]</code>."
          example="e.g. (555) 123-4567"
          :value="localData.officePhone"
          :validation="officePhoneValidation"
        >
          <input
            v-model="localData.officePhone"
            class="a2p-field-input mono"
            placeholder="(555) 123-4567"
            required
          />
        </A2PField>

        <A2PField
          :num="10"
          label="Office Email"
          help="A monitored office email for SMS opt-out requests. Gets inserted where the compliance text says <code class='inline'>[OFFICE EMAIL]</code>."
          example="e.g. hello@smiledental.com"
          :value="localData.officeEmail"
          :validation="officeEmailValidation"
        >
          <input
            v-model="localData.officeEmail"
            type="email"
            class="a2p-field-input mono"
            placeholder="hello@smiledental.com"
            required
          />
        </A2PField>

        <A2PField
          :num="11"
          label="Message frequency"
          help="Typical number of messages a patient will receive per month. Shown verbatim in the compliance text."
          :value="localData.messageFrequency"
        >
          <select v-model="localData.messageFrequency" class="a2p-field-select">
            <option v-for="opt in MESSAGE_FREQUENCY_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </A2PField>
      </div>

      <div class="a2p-actions">
        <button type="button" class="a2p-btn a2p-btn-secondary" @click="$emit('back')">
          Back
        </button>
        <button
          type="submit"
          class="a2p-btn a2p-btn-primary"
          :disabled="!canContinue"
        >
          Continue
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions } from 'vuex';
import mixin from '@/mixin';
import A2PField from './components/A2PField.vue';
import A2PUrlScanResult from './components/A2PUrlScanResult.vue';
import {
  validateLegalName,
  validateTaxId,
  validateWebsite,
  validatePhone,
  validateOfficeEmail,
  MESSAGE_FREQUENCY_OPTIONS,
} from '@/utils/a2pValidation';

const SCAN_DEBOUNCE_MS = 800;

function emptyScanState() {
  return { state: 'idle', result: {}, error: '', lastScannedUrl: '' };
}

export default {
  name: 'A2PWizardBusinessInfo',
  components: { A2PField, A2PUrlScanResult },
  mixins: [mixin],
  props: [
    'eyebrow',
    'businessName',
    'businessAddress',
    'ein',
    'businessType',
    'website',
    'country',
    'privacyPolicyURL',
    'termsOfServiceURL',
    'optInScreenshotURL',
    'messageFrequency',
    'officePhone',
    'officeEmail',
    // Set by the wizard when filing on behalf of another practice — suppresses
    // ambient prefills sourced from the operator's own site. See created().
    'isCrossSite',
  ],
  data() {
    return {
      localData: {
        businessName: this.businessName || '',
        businessAddress: this.businessAddress || '',
        ein: this.ein || '',
        businessType: this.businessType || '',
        website: this.website || '',
        country: this.country || 'US',
        privacyPolicyURL: this.privacyPolicyURL || '',
        termsOfServiceURL: this.termsOfServiceURL || '',
        optInScreenshotURL: this.optInScreenshotURL || '',
        messageFrequency: this.messageFrequency || 'Up to 8 msgs/month',
        officePhone: this.officePhone || '',
        officeEmail: this.officeEmail || '',
      },
      MESSAGE_FREQUENCY_OPTIONS,
      // Live URL scan state — one per URL field
      scan: {
        website: emptyScanState(),
        privacy: emptyScanState(),
        terms: emptyScanState(),
      },
      // Debounce timers + cancel tokens per field, not reactive (attached to this directly below)
    };
  },
  // Debounce timers + cancel tokens: non-reactive instance state, set in created()
  computed: {
    taxIdLabel() {
      return this.localData.country === 'CA' ? 'Business Number (BN)' : 'Tax ID (EIN)';
    },
    taxIdPlaceholder() {
      return this.localData.country === 'CA' ? '123456789' : 'XX-XXXXXXX';
    },
    taxIdExample() {
      return this.localData.country === 'CA' ? 'e.g. 123456789' : 'e.g. 12-3456789';
    },
    taxIdHelp() {
      return this.localData.country === 'CA'
        ? '<strong>Canada:</strong> Business Number — first 9 digits only, no dashes.'
        : '<strong>US:</strong> EIN formatted as <code class="inline">XX-XXXXXXX</code> (9 digits with a dash).';
    },
    privacyUrlHelp() {
      return 'If your page lives at <code class="inline">yourwebsite.com/privacy</code>, leave blank — we\'ll use that by default. Send only if it\'s at a different path.';
    },
    termsUrlHelp() {
      return 'Same rule — we\'ll default to <code class="inline">yourwebsite.com/terms</code> unless you tell us otherwise.';
    },

    legalNameValidation() { return validateLegalName(this.localData.businessName); },
    taxIdValidation()     { return validateTaxId(this.localData.ein, this.localData.country); },
    websiteValidation()   { return validateWebsite(this.localData.website); },
    officePhoneValidation() { return validatePhone(this.localData.officePhone); },
    officeEmailValidation() { return validateOfficeEmail(this.localData.officeEmail); },

    canContinue() {
      const d = this.localData;
      if (!d.businessName.trim()) return false;
      if (!d.businessAddress.trim()) return false;
      if (!d.businessType) return false;
      if (!d.officePhone.trim()) return false;
      if (!d.officeEmail.trim()) return false;
      const tax = this.taxIdValidation; if (!tax || tax.level !== 'ok') return false;
      const web = this.websiteValidation; if (!web || web.level !== 'ok') return false;
      const phone = this.officePhoneValidation; if (!phone || phone.level !== 'ok') return false;
      const email = this.officeEmailValidation; if (!email || email.level !== 'ok') return false;
      return true;
    },
  },
  created() {
    // Auto-fill office phone from the site's GENERAL_PHONE setting when unset.
    //
    // `allSettings` is the LOGGED-IN operator's site settings, not this
    // registration's site — so this prefill is only correct when a practice is
    // filing for itself. While cross-site (super-admin filing for another
    // practice) it leaked the operator's number into the customer's carrier
    // registration AND into the opt-out text that customer publishes on their
    // own site. Verified 2026-08-26: Summerbrook Dental (Fort Worth, TX) was
    // pre-filled with Mint Dental of Emerson's +1 201-834-7259.
    // Leaving the field empty is correct: it is `required`, so the operator is
    // forced to enter the practice's real public number.
    if (!this.isCrossSite && !this.localData.officePhone && this.allSettings && this.allSettings.GENERAL_PHONE) {
      const val = this.allSettings.GENERAL_PHONE.PropertyValue;
      if (val && val.trim()) this.localData.officePhone = val.trim();
    }
    // Non-reactive instance state — debounce timers + cancel tokens per URL field.
    // Kept off `data` so Vue's reactive system doesn't wrap timer handles.
    this._scanTimers = { website: null, privacy: null, terms: null };
    this._scanCancelSources = { website: null, privacy: null, terms: null };
    this._scanCache = {}; // keyed by `${urlType}|${url}` → last result, avoids re-hitting backend
  },
  beforeDestroy() {
    ['website', 'privacy', 'terms'].forEach((field) => {
      if (this._scanTimers && this._scanTimers[field]) clearTimeout(this._scanTimers[field]);
      if (this._scanCancelSources && this._scanCancelSources[field]) {
        try { this._scanCancelSources[field].cancel(); } catch (e) { /* ignore */ }
      }
    });
  },
  methods: {
    ...mapActions('a2p', ['checkComplianceUrl']),
    onCountryChange() {
      this.localData.ein = '';
    },
    formatTaxId(e) {
      const raw = e && e.target ? e.target.value : this.localData.ein;
      if (this.localData.country === 'CA') {
        this.localData.ein = String(raw).replace(/\D/g, '').substring(0, 9);
      } else {
        let cleaned = String(raw).replace(/\D/g, '').substring(0, 9);
        this.localData.ein = cleaned.length > 2
          ? cleaned.substring(0, 2) + '-' + cleaned.substring(2)
          : cleaned;
      }
    },
    onWebsiteChange() {
      // clear auto-generated URLs if site changes
      if (this.localData.privacyPolicyURL && this.localData.privacyPolicyURL.includes(this.localData.website)) {
        this.localData.privacyPolicyURL = '';
      }
      if (this.localData.termsOfServiceURL && this.localData.termsOfServiceURL.includes(this.localData.website)) {
        this.localData.termsOfServiceURL = '';
      }
    },

    // Input handlers — reset state and schedule a debounced scan
    onWebsiteInput() {
      this.onWebsiteChange();
      this.scheduleScan('website');
    },
    onPrivacyInput() { this.scheduleScan('privacy'); },
    onTermsInput()   { this.scheduleScan('terms'); },

    urlFor(field) {
      if (field === 'website') return this.localData.website;
      if (field === 'privacy') return this.localData.privacyPolicyURL;
      if (field === 'terms')   return this.localData.termsOfServiceURL;
      return '';
    },

    isScannableUrl(url) {
      // Only scan once the user has typed enough of a URL to be meaningful.
      // Matches http(s)://anything.tld at a minimum.
      return /^https?:\/\/.+\..+/.test(url);
    },

    scheduleScan(field) {
      // Reset to idle while they're typing; debounce kicks the actual scan.
      if (this._scanTimers[field]) clearTimeout(this._scanTimers[field]);
      const url = (this.urlFor(field) || '').trim();
      if (!this.isScannableUrl(url)) {
        this.scan[field] = emptyScanState();
        return;
      }
      this._scanTimers[field] = setTimeout(() => this.runScanNow(field), SCAN_DEBOUNCE_MS);
    },

    async runScanNow(field) {
      const url = (this.urlFor(field) || '').trim();
      if (!this.isScannableUrl(url)) {
        this.scan[field] = emptyScanState();
        return;
      }
      // Cache: skip the network round-trip if we've already scanned this exact URL.
      const cacheKey = `${field}|${url}`;
      if (this._scanCache[cacheKey]) {
        this.scan[field] = { ...this._scanCache[cacheKey], lastScannedUrl: url };
        return;
      }
      // Cancel any in-flight scan on this field so the latest typed URL wins.
      if (this._scanCancelSources[field]) {
        try { this._scanCancelSources[field].cancel(); } catch (e) { /* ignore */ }
      }
      const source = axios.CancelToken.source();
      this._scanCancelSources[field] = source;
      this.scan[field] = { state: 'checking', result: {}, error: '', lastScannedUrl: url };

      const res = await this.checkComplianceUrl({
        url,
        urlType: field,
        cancelToken: source.token,
      });

      // Ignore if superseded by a newer scan OR the user has since typed a different URL
      if (res && res.cancelled) return;
      if (this.urlFor(field).trim() !== url) return;

      let newState;
      if (!res || res.success === false) {
        newState = {
          state: 'error',
          result: {},
          error: (res && res.error) || 'Something went wrong',
          lastScannedUrl: url,
        };
      } else if (!res.exists) {
        newState = {
          state: 'error',
          result: {},
          error: res.error || 'Page not found or unreachable',
          lastScannedUrl: url,
        };
      } else if (field === 'website') {
        newState = { state: 'website-ok', result: res, error: '', lastScannedUrl: url };
      } else {
        newState = { state: 'page-scan', result: res, error: '', lastScannedUrl: url };
      }
      this.scan[field] = newState;
      this._scanCache[cacheKey] = newState;
    },

    submit() {
      if (!this.canContinue) return;
      this.$emit('next', { ...this.localData });
    },
  },
};
</script>

<style src="./a2p-wizard.css"></style>
